import { ProductTypeMap, ProductTypeDetails } from '@/api/producttypes';
import { ProductMapping } from '@/api/productmapping';
import { Qualifier } from './Qualifier';
import { IQualifier } from './IQualifier';
import { LoanQualifierType } from './LoanQualifierType';
import { LoanQualifierProduct } from './LoanQualifierProduct';

export class LoanQualifier extends Qualifier {
  public static readonly CLASS: string = 'com.bancvue.rcx.config.product.qualifiers.api.LoanQualifier';

  public static readonly NAME: string = 'Loan';

  countOfLoans: number = 1;

  loanTypes: LoanQualifierType[] = [];

  loanProducts: LoanQualifierProduct[] = [];

  constructor(qual?: IQualifier) {
    super(LoanQualifier.CLASS, qual ? qual.id : null);

    if (qual) {
      this.countOfLoans = (qual as any).countOfLoans;
      this.loanTypes = (qual as any).loanTypes;
      this.loanProducts = (qual as any).loanProducts;
    }
  }

  getDescription(productTypeMap: ProductTypeMap, productMappings: ProductMapping[]): string {
    let description = `${this.countOfLoans} loan`;
    if (this.countOfLoans > 1) {
      description += 's';
    }

    if (this.loanTypes && this.loanTypes.length > 0) {
      description += this.buildLoanTypeDescriptions(productTypeMap);
    } else if (this.loanProducts && this.loanProducts.length > 0) {
      description += this.buildLoanProductDescriptions(productMappings);
    }

    return description;
  }

  buildLoanTypeDescriptions(productTypeMap: ProductTypeMap): string {
    let description: string = ' (';
    const typeNames: string[] = [];
    this.loanTypes.forEach((loanType: LoanQualifierType) => {
      const loanTypeDetails: ProductTypeDetails | undefined = productTypeMap[loanType.productType];

      if (loanTypeDetails) {
        typeNames.push(loanTypeDetails.displayName);
      } else {
        typeNames.push(loanType.productType);
      }
    });
    description += `${typeNames.join(', ')})`;
    return description;
  }

  buildLoanProductDescriptions(productMappings: ProductMapping[]): string {
    let description: string = ' (';
    const productNames: string[] = [];
    this.loanProducts.forEach((loanProduct: LoanQualifierProduct) => {
      const productMapping: ProductMapping | null = LoanQualifier.findProductMapping(productMappings, loanProduct.externalProductMappingId);

      if (productMapping) {
        productNames.push(productMapping.fiProductName);
      } else {
        productNames.push(loanProduct.externalProductMappingId.toString());
      }
    });
    description += `${productNames.join(', ')})`;
    return description;
  }

  static findProductMapping(productMappings: ProductMapping[], externalProductMappingId: number): ProductMapping | null {
    for (let i = 0; i < productMappings.length; i++) {
      if (productMappings[i].externalProductMappingId === externalProductMappingId) {
        return productMappings[i];
      }
    }
    return null;
  }

  clone(): IQualifier {
    const clone = new LoanQualifier();
    Object.assign(clone, this);
    clone.loanProducts = this.cloneLoanProducts();
    clone.loanTypes = this.cloneLoanTypes();
    return clone;
  }

  cloneLoanProducts(): LoanQualifierProduct[] {
    const clonedProducts : LoanQualifierProduct[] = [];
    this.loanProducts.forEach((loanProduct) => {
      const clone = Object.assign({}, loanProduct);
      clonedProducts.push(clone);
    });
    return clonedProducts;
  }

  cloneLoanTypes(): LoanQualifierType[] {
    const clonedTypes : LoanQualifierType[] = [];
    this.loanTypes.forEach((loanType) => {
      const clone = Object.assign({}, loanType);
      clonedTypes.push(clone);
    });
    return clonedTypes;
  }
}

export default LoanQualifier;
